/**
 * Formats a date value for display in DD-MM-YYYY format. Returns null for missing/invalid dates
 * so KeyDataRow can show "No Record".
 */
export function formatDate(value: string | Date | null | undefined): string | null {
  if (value == null) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  const year = date.getUTCFullYear()
  if (year < 100) return null
  const day = String(date.getUTCDate()).padStart(2, '0')
  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
  return `${month}-${day}-${year}`
}

/**
 * Formats a money value for display with thousand separators.
 * If currency is provided, formats with its symbol (e.g. "£1,234.56"); otherwise formats as a plain decimal (e.g. "1,234.56").
 * Returns null for missing values so KeyDataRow can show "No Record".
 * Zero values display as "0.00" or "$0.00" (not null).
 */
function isValidCurrencyCode(code: string): boolean {
  try {
    new Intl.NumberFormat(undefined, { style: 'currency', currency: code })
    return true
  } catch {
    return false
  }
}

export function formatCurrency(money: { amount: number } | null | undefined, currency?: string): string | null {
  if (money == null) return null
  const validCurrency = currency && isValidCurrencyCode(currency) ? currency : undefined
  const options: Intl.NumberFormatOptions = validCurrency
    ? { style: 'currency', currency: validCurrency }
    : { style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2 }
  return new Intl.NumberFormat(undefined, options).format(money.amount)
}

/**
 * Formats a raw byte count string for display (e.g. "1234567" → "1.2 MB").
 * Returns "—" for missing or invalid values.
 */
export function formatFileSize(bytes: string | undefined): string {
  if (!bytes) return '—'
  const n = Number(bytes)
  if (Number.isNaN(n)) return '—'
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}
